import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';


const ALBUM_TYPE_OPTIONS = [
  { value: 1, label: '싱글' },
  { value: 2, label: 'EP' },
  { value: 3, label: '정규' },
  { value: 4, label: '컴필레이션' },
];

export default function ReviewModifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentPage = parseInt(params.get('page')) || 1;

  const { id } = useParams();
  const { me } = useSelector(authSelector.selectAuthState);

  // member 제출건 여부
  const [isMemberReview, setIsMemberReview] = useState(false);

  // 앨범 기본 정보
  const [album, setAlbum] = useState(null);
  const [albumName, setAlbumName] = useState('');
  const [albumArtist, setAlbumArtist] = useState('');
  const [albumClient, setAlbumClient] = useState('');
  const [memberName, setMemberName] = useState('');
  const [albumReleaseDate, setAlbumReleaseDate] = useState('');
  const [albumType, setAlbumType] = useState('');
  const [albumLoading, setAlbumLoading] = useState(false);

  // 앨범 추가 입력
  const [physicalAlbumYn, setPhysicalAlbumYn] = useState('N');
  const [riakId, setRiakId] = useState('');
  const [riakProducerName, setRiakProducerName] = useState('');

  // 곡 정보
  const [songs, setSongs] = useState([]);
  const [songInputs, setSongInputs] = useState({});

  // 가사 입력 모달
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [currentSongId, setCurrentSongId] = useState(null);
  const [tempLyrics, setTempLyrics] = useState('');
  const [tempLyricsTranslation, setTempLyricsTranslation] = useState('');
  const [hasTranslation, setHasTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);

  // 심의결과 모달
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [broadcasters, setBroadcasters] = useState([]);
  const [results, setResults] = useState({});
  const [currentDetailId, setCurrentDetailId] = useState(null);

  // 데이터 불러오기
  useEffect(() => {
    const fetchData = async () => {
      setAlbumLoading(true);
      try {
        const res = await axios.get(`/reviewApply/${id}`);
        const { album, songs, client, memberReviewAlbum, member } = res.data;

        const mra = memberReviewAlbum;
        const isMember = !!member;
        setIsMemberReview(isMember);

        if (isMember) {
          setAlbum({ nameKr: mra?.albumName });
          setAlbumName(mra?.albumName || '');
          setAlbumArtist(mra?.artistName || '');
          setAlbumClient(mra?.clientName || '');
          setMemberName(member?.name || '');
          setAlbumReleaseDate(mra?.releaseDate || '');
          setAlbumType(mra?.albumTypeId || '');
          setPhysicalAlbumYn(mra?.physicalAlbumYn || 'N');
          setRiakId(mra?.riakId || '');
          setRiakProducerName(mra?.riakProducerName || '');
        } else {
          setAlbum(album);
          setAlbumName(album?.nameKr || '');
          setAlbumArtist(album?.artist?.nameKr || '');
          setAlbumClient(client?.name || '');
          setAlbumReleaseDate(album?.releaseDateTime || '');
          setAlbumType(album?.albumTypeId || '');
          setPhysicalAlbumYn(album?.physicalAlbumYn || 'N');
          setRiakId(client?.riakId || '');
          setRiakProducerName(client?.riakProducerName || '');
        }

        const hasSongData = songs?.length > 0 && songs.some((s) => s.nameKr);
        const songList = hasSongData ? songs : (mra?.memberReviewSongs || []);

        const initInputs = {};
        songList.forEach((s) => {
          initInputs[s.id] = {
            titleYn: s.titleYn || 'N',
            track: s.track || '',
            nameKr: s.nameKr || '',
            writerKr: s.writerKr || '',
            composerKr: s.composerKr || '',
            arrangerKr: s.arrangerKr || '',
            featuring: s.featuring || '',
            featuringLatestRelease: s.featuringLatestRelease || '',
            performance: s.performance || '',
            woneumYn: s.woneumYn === 'Y',
            lyrics: s.lyrics || '',
            lyricsTranslation: s.lyricsTranslation || '',
            results: s.reviewApplyResults || [],
          };
        });

        setSongs(songList);
        setSongInputs(initInputs);
      } catch (e) {
        console.error(e);
      } finally {
        setAlbumLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // 곡 입력 업데이트
  const updateSongInput = (songId, field, value) => {
    setSongInputs((prev) => ({
      ...prev,
      [songId]: { ...(prev[songId] || {}), [field]: value },
    }));
  };

  // 원음방송 선택
  const updatewoneumYn = (songId, checked) => {
    const selectedCount = Object.values(songInputs).filter(
      (si) => si.woneumYn
    ).length;
    if (checked && selectedCount >= 3) {
      alert('원음방송은 최대 3곡까지 선택할 수 있습니다.');
      return;
    }
    updateSongInput(songId, 'woneumYn', checked);
  };

  // 가사 모달
  const openLyricsModal = (songId) => {
    setCurrentSongId(songId);
    setTempLyrics(songInputs[songId]?.lyrics || '');
    const trans = songInputs[songId]?.lyricsTranslation || '';
    setTempLyricsTranslation(trans);
    setHasTranslation(!!trans);
    setLyricsModalOpen(true);
  };
  const closeLyricsModal = () => {
    setLyricsModalOpen(false);
    setCurrentSongId(null);
    setTempLyrics('');
    setTempLyricsTranslation('');
    setHasTranslation(false);
    setTranslating(false);
  };
  const autoTranslate = async () => {
    if (!tempLyrics.trim()) return;
    setHasTranslation(true);
    setTranslating(true);
    try {
      const res = await axios.post('/api/public/reviewApply/translate-lyrics', { lyrics: tempLyrics });
      setTempLyricsTranslation(res.data.translation || '');
    } catch (e) {
      alert('번역 중 오류가 발생했습니다.');
    } finally {
      setTranslating(false);
    }
  };
  const saveLyricsModal = () => {
    if (currentSongId) {
      updateSongInput(currentSongId, 'lyrics', tempLyrics);
      updateSongInput(currentSongId, 'lyricsTranslation', hasTranslation ? tempLyricsTranslation : '');
    }
    closeLyricsModal();
  };

  // 제출 가능 여부
  const validationErrors = useMemo(() => {
    const errors = [];
    if (isMemberReview) {
      if (!albumName.trim()) errors.push('앨범명');
      if (!albumClient.trim()) errors.push('권리사명');
      if (!albumArtist.trim()) errors.push('아티스트명');
      if (!albumReleaseDate) errors.push('발매일');
      if (!albumType) errors.push('앨범 타입');
    } else {
      if (!album?.id && !album?.nameKr) errors.push('앨범 정보');
    }
    if (!songs.length) { errors.push('곡 정보 없음'); return errors; }

    for (let i = 0; i < songs.length; i++) {
      const s = songs[i];
      const input = songInputs[s.id] || {};
      const t = `${i + 1}번 곡`;
      if (isMemberReview) {
        if (!input.nameKr?.trim()) errors.push(`${t}: 곡명`);
        if (!input.composerKr?.trim()) errors.push(`${t}: 작곡`);
      }
      if (!input.performance?.trim()) errors.push(`${t}: 실연(연주자명/악기명)`);
      const songName = isMemberReview ? input.nameKr : s.nameKr;
      if (!(songName || '').includes('Inst.') && !input.lyrics?.trim()) errors.push(`${t}: 가사`);
    }

    const bcCount = Object.values(songInputs).filter((si) => si.woneumYn).length;
    if (bcCount === 0) errors.push('원음방송 최소 1곡 선택');
    if (bcCount > 3) errors.push('원음방송 최대 3곡');

    return errors;
  }, [album, albumName, albumClient, albumArtist, albumReleaseDate, albumType, isMemberReview, songs, songInputs]);

  const canSubmit = validationErrors.length === 0;

  // 수정 요청
  const onSubmit = async () => {
    if (!canSubmit) {
      alert('필수 항목을 확인해 주세요.\n\n누락: ' + validationErrors.join(', '));
      return;
    }
    try {
      const payload = isMemberReview ? {
        albumName: albumName.trim(),
        clientName: albumClient.trim(),
        artistName: albumArtist.trim(),
        releaseDate: albumReleaseDate,
        albumTypeId: Number(albumType),
        physicalAlbumYn,
        riakId: riakId.trim(),
        riakProducerName: riakProducerName.trim(),
        songs: songs.map((s) => ({
          memberReviewSongId: s.id,
          titleYn: songInputs[s.id]?.titleYn || 'N',
          track: songInputs[s.id]?.track,
          nameKr: songInputs[s.id]?.nameKr?.trim(),
          writerKr: songInputs[s.id]?.writerKr?.trim(),
          composerKr: songInputs[s.id]?.composerKr?.trim(),
          arrangerKr: songInputs[s.id]?.arrangerKr?.trim(),
          featuring: (songInputs[s.id]?.featuring || '').trim(),
          featuringLatestRelease: (songInputs[s.id]?.featuringLatestRelease || '').trim(),
          performance: (songInputs[s.id]?.performance || '').trim(),
          woneumYn: songInputs[s.id]?.woneumYn || false,
          lyrics: (songInputs[s.id]?.lyrics || '').trim(),
          lyricsTranslation: (songInputs[s.id]?.lyricsTranslation || '').trim() || null,
        })),
        isMemberReview: true,
      } : {
        clientId: me?.clientId,
        albumId: album.id,
        physicalAlbumYn,
        riakId: riakId.trim(),
        riakProducerName: riakProducerName.trim(),
        songs: songs.map((s) => ({
          songId: s.id,
          featuring: (songInputs[s.id]?.featuring || '').trim(),
          featuringLatestRelease: (songInputs[s.id]?.featuringLatestRelease || '').trim(),
          performance: (songInputs[s.id]?.performance || '').trim(),
          woneumYn: songInputs[s.id]?.woneumYn || false,
          lyrics: (songInputs[s.id]?.lyrics || '').trim(),
          lyricsTranslation: (songInputs[s.id]?.lyricsTranslation || '').trim() || null,
        })),
      };
      await axios.put(`/reviewApply/${id}`, payload);
      if (window.confirm('신청서가 수정되었습니다.')) {
        navigate(`/ReviewViewPage/${id}?page=${currentPage}`);
      }
    } catch (e) {
      console.error(e);
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  return (
    <>
      {/* 제목 */}
      <div className="mb-8 text-center">
          <div className="text-xl sm:text-2xl font-bold mb-3 text-white tracking-tight">신청서 수정</div>
        </div>

        <div className="space-y-6 max-w-3xl mx-auto">
          {/* 앨범 정보 */}
          <div className="grid grid-cols-1 gap-4">
            {me?.adminYn === 'Y' && isMemberReview && (
              <ReadOnlyField label="회원명" value={memberName} loading={albumLoading} />
            )}
            {me?.adminYn === 'Y' && !isMemberReview && (
              <ReadOnlyField label="권리사명" value={albumClient} loading={albumLoading} />
            )}
            {isMemberReview ? (
              <>
                <InputField label="권리사명" required value={albumClient} onChange={(e) => setAlbumClient(e.target.value)} />
                <InputField label="앨범명" required value={albumName} onChange={(e) => setAlbumName(e.target.value)} />
                <InputField label="아티스트명" required value={albumArtist} onChange={(e) => setAlbumArtist(e.target.value)} />
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    발매일 <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={albumReleaseDate} onChange={(e) => setAlbumReleaseDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    앨범 타입 <span className="text-red-500">*</span>
                  </label>
                  <select value={albumType} onChange={(e) => setAlbumType(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent">
                    <option value="">선택</option>
                    {ALBUM_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <ReadOnlyField label="앨범명" value={album?.nameKr} />
                <ReadOnlyField label="아티스트명" value={albumArtist} />
                <ReadOnlyField label="발매일" value={albumReleaseDate} />
                <ReadOnlyField
                  label="앨범 타입"
                  value={ALBUM_TYPE_OPTIONS.find((o) => o.value === albumType)?.label || ''}
                />
              </>
            )}
          </div>

          {/* 앨범 추가 입력 */}
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-4 bg-[#182334]">
            <InputField
              label="RIAK 아이디"
              value={riakId}
              onChange={(e) => setRiakId(e.target.value)}
              placeholder="예) pink"
            />
            <InputField
              label="RIAK 제작사명"
              value={riakProducerName}
              onChange={(e) => setRiakProducerName(e.target.value)}
              placeholder="예) 핑크페이퍼"
            />
          </div>

          {/* 구분선 */}
          <div>
            <div className="flex items-center my-10">
              <div className="flex-grow border-t border-white/[0.08]"></div>
              <span className="px-3 text-slate-300 font-bold">곡 정보</span>
              <div className="flex-grow border-t border-white/[0.08]"></div>
            </div>
          </div>

          {/* 곡 리스트 */}
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-6 bg-white/[0.03] mt-8">
            {songs.map((s) => (
              <div
                key={s.id}
                className="rounded-xl border border-white/[0.08] p-4 space-y-3 bg-[#182334]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {isMemberReview ? (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                          타이틀YN <span className="text-red-500">*</span>
                        </label>
                        <select value={songInputs[s.id]?.titleYn || 'N'} onChange={(e) => updateSongInput(s.id, 'titleYn', e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent">
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </select>
                      </div>
                      <InputField label="트랙" required value={songInputs[s.id]?.track || ''} onChange={(e) => updateSongInput(s.id, 'track', e.target.value)} />
                      <InputField label="곡명" required value={songInputs[s.id]?.nameKr || ''} onChange={(e) => updateSongInput(s.id, 'nameKr', e.target.value)} />
                      <InputField label="작사" value={songInputs[s.id]?.writerKr || ''} onChange={(e) => updateSongInput(s.id, 'writerKr', e.target.value)} />
                      <InputField label="작곡" required value={songInputs[s.id]?.composerKr || ''} onChange={(e) => updateSongInput(s.id, 'composerKr', e.target.value)} />
                      <InputField label="편곡" value={songInputs[s.id]?.arrangerKr || ''} onChange={(e) => updateSongInput(s.id, 'arrangerKr', e.target.value)} />
                    </>
                  ) : (
                    <>
                      <ReadOnlyField label="타이틀YN" value={s.titleYn} />
                      <ReadOnlyField label="트랙" value={s.track} />
                      <ReadOnlyField label="곡명" value={s.nameKr} />
                      <ReadOnlyField label="작사" value={s.writerKr} />
                      <ReadOnlyField label="작곡" value={s.composerKr} />
                      <ReadOnlyField label="편곡" value={s.arrangerKr} />
                    </>
                  )}
                </div>

                {s.fileUrl && (
                  <div>
                    <label className="block font-semibold mb-1">
                      음원 듣기
                    </label>
                    <audio controls src={s.fileUrl} style={{ width: '100%' }} />
                  </div>
                )}

                <InputField
                  label="피처링"
                  value={songInputs[s.id]?.featuring || ''}
                  onChange={(e) =>
                    updateSongInput(s.id, 'featuring', e.target.value)
                  }
                />
                <InputField
                  label="피처링 아티스트의 최근 음원"
                  value={songInputs[s.id]?.featuringLatestRelease || ''}
                  onChange={(e) =>
                    updateSongInput(
                      s.id,
                      'featuringLatestRelease',
                      e.target.value
                    )
                  }
                />
                <InputField
                  label="실연(연주자명/악기명)"
                  required
                  value={songInputs[s.id]?.performance || ''}
                  onChange={(e) =>
                    updateSongInput(s.id, 'performance', e.target.value)
                  }
                />

                {!((isMemberReview ? songInputs[s.id]?.nameKr : s.nameKr) || '').includes('Inst.') && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                      가사 <span className="text-red-500">*</span>
                    </label>
                    <button
                      onClick={() => openLyricsModal(s.id)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                        songInputs[s.id]?.lyrics ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                      }`}>
                      {songInputs[s.id]?.lyrics ? '가사 수정' : '가사 입력'}
                    </button>
                  </div>
                )}

                {/* 원음방송 선택 */}
                <label className="flex items-center gap-2 cursor-pointer font-semibold">
                  <input
                    type="checkbox"
                    checked={songInputs[s.id]?.woneumYn || false}
                    onChange={(e) => updatewoneumYn(s.id, e.target.checked)}
                    className="peer hidden"
                  />
                  <span className="w-6 h-6 flex items-center justify-center border border-white/[0.08] rounded-full peer-checked:bg-sky-500 peer-checked:border-sky-500 text-white">
                    ✓
                  </span>
                  <span>
                    원음방송 선택 (최대 3곡){' '}
                    <span className="text-red-500">*</span>
                  </span>
                </label>

                {/* 관리자 전용: 심의결과 입력 버튼 */}
                {me?.adminYn === 'Y' && (
                  <div className="mt-3">
                    <button
                      className="w-full px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors"
                      onClick={async () => {
                        try {
                          const [bcRes, resultRes] = await Promise.all([
                            axios.get(`/reviewApply/broadcaster`),
                            axios.get(`/reviewApply/${s.reviewApplyDetailId}/result`),
                          ]);
                          const bcList = bcRes.data.map((b) => ({ id: b.id, name: b.name }));
                          setBroadcasters(bcList);

                          const initResults = {};
                          resultRes.data.forEach((r) => {
                            initResults[r.broadcasterId] = { status: r.status, remark: r.remark };
                          });
                          // 결과 없는 방송사는 심의중(WAITING) 기본값
                          bcList.forEach((b) => {
                            if (!initResults[b.id]) initResults[b.id] = { status: 'WAITING', remark: '' };
                          });
                          setResults(initResults);

                          setCurrentDetailId(s.reviewApplyDetailId);
                          setResultModalOpen(true);
                        } catch (err) {
                          console.error(err);
                          alert('심의결과 불러오기 실패');
                        }
                      }}>
                      심의결과 입력
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 버튼 */}
          <div className="flex flex-wrap justify-end items-center gap-2 mt-6">
            <button
              onClick={onSubmit}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                canSubmit ? 'bg-sky-600 text-white hover:bg-sky-500' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
              }`}>
              수정완료
            </button>
            <button
              onClick={() => navigate('/ReviewPage')}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
              목록
            </button>
        </div>
      </div>

      {/* 가사 모달 */}
      {lyricsModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-[#182334] rounded-lg p-6 w-3/4 h-3/4 flex flex-col border border-white/[0.08]">
            <h2 className="text-lg font-semibold mb-2 text-white">가사 입력</h2>
            <textarea
              value={tempLyrics}
              onChange={(e) => setTempLyrics(e.target.value)}
              className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-white focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent resize-none ${hasTranslation ? 'flex-none h-2/5' : 'flex-1'}`}
            />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <input type="checkbox" id="hasTranslationCheck" checked={hasTranslation} onChange={(e) => setHasTranslation(e.target.checked)} className="w-4 h-4 accent-sky-400" />
              <label htmlFor="hasTranslationCheck" className="text-sm text-slate-300 cursor-pointer">한글번역 포함</label>
              <button onClick={autoTranslate} disabled={translating || !tempLyrics.trim()}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {translating ? '번역 중...' : '✦ 자동번역'}
              </button>
            </div>
            {hasTranslation && (
              <textarea
                value={translating ? '번역 중...' : tempLyricsTranslation}
                onChange={(e) => setTempLyricsTranslation(e.target.value)}
                readOnly={translating}
                className="flex-1 w-full mt-2 px-3 py-2 rounded-xl border border-sky-500/30 bg-[#1a2b42] text-white focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none"
                placeholder="한글번역 가사를 입력해 주세요."
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={closeLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">취소</button>
              <button onClick={saveLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 transition-colors">저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 심의결과 모달 */}
      {resultModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-[#182334] rounded-lg p-6 w-full max-w-2xl mx-4 flex flex-col space-y-4 border border-white/[0.08]">
            <h2 className="text-lg font-bold text-center mb-4">
              심의결과 입력
            </h2>

            {/* 전체 버튼 */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => {
                  const updated = {};
                  broadcasters.forEach((b) => {
                    updated[b.id] = { ...results[b.id], status: 'Y' };
                  });
                  setResults(updated);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors">
                전체 통과
              </button>
              <button
                onClick={() => {
                  const updated = {};
                  broadcasters.forEach((b) => {
                    updated[b.id] = { ...results[b.id], status: 'N' };
                  });
                  setResults(updated);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 transition-colors">
                전체 거절
              </button>
            </div>

            {/* 방송국별 리스트 */}
            <div className="space-y-3 max-h-[520px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:transparent [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full">
              {broadcasters.map((b) => (
                <div
                  key={b.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border border-white/[0.08] p-3 rounded-xl">
                  <span className="font-bold text-white text-sm">
                    📻 {b.name}
                  </span>

                  <div className="flex flex-col sm:flex-row gap-2 flex-1 justify-end items-center">
                    {['WAITING', 'Y', 'N'].map((st) => (
                      <button
                        key={st}
                        onClick={() =>
                          setResults((prev) => ({
                            ...prev,
                            [b.id]: { ...prev[b.id], status: st },
                          }))
                        }
                        className={`px-3 py-1 rounded-xl text-xs font-bold transition-colors ${
                          results[b.id]?.status === st
                            ? st === 'Y' ? 'bg-sky-500 text-white' : st === 'N' ? 'bg-red-500 text-white' : 'bg-slate-500 text-white'
                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                        }`}>
                        {st === 'Y' ? '통과' : st === 'N' ? '거절' : '심의중'}
                      </button>
                    ))}
                    <input
                      type="text"
                      placeholder="사유"
                      value={results[b.id]?.remark || ''}
                      onChange={(e) =>
                        setResults((prev) => ({
                          ...prev,
                          [b.id]: { ...prev[b.id], remark: e.target.value },
                        }))
                      }
                      className="w-32 sm:w-40 px-2 py-1 text-sm rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 focus:outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={async () => {
                  await axios.put(`/reviewApply/${currentDetailId}/result`, {
                    results: Object.entries(results).map(
                      ([broadcasterId, r]) => ({
                        broadcasterId,
                        status: r.status || 'WAITING',
                        remark: r.remark,
                      })
                    ),
                  });
                  alert('심의 결과가 저장되었습니다.');
                  setResultModalOpen(false);
                }}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 transition-colors">
                저장
              </button>
              <button
                onClick={() => setResultModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// 읽기 전용 input
function ReadOnlyField({ label, value, loading }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <input
        type="text"
        value={loading ? '불러오는 중…' : value ?? ''}
        className="w-full px-3 py-2 rounded-xl border bg-white/5 border-white/[0.08] text-slate-400 text-sm focus:outline-none"
        readOnly
      />
    </div>
  );
}

// 일반 input
function InputField({ label, value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
        placeholder={placeholder}
      />
    </div>
  );
}
